import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';

interface DateInputProps {
  value?: string; // ISO date string (yyyy-MM-dd)
  onChange: (value: string) => void; // Returns ISO date string
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  minDate?: string; // ISO date string (yyyy-MM-dd) - dates before this will be disabled
  maxDate?: string; // ISO date string (yyyy-MM-dd) - dates after this will be disabled
  pickerClassName?: string;
}

const mobileDatePickerQuery = '(max-width: 768px), (pointer: coarse)';

const shouldUseNativeDatePicker = (): boolean => (
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(mobileDatePickerQuery).matches
);

/**
 * Custom date input component with datepicker that displays dates in dd.MM.yyyy format
 * while maintaining ISO format (yyyy-MM-dd) for internal value
 */
const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  className = '',
  id,
  name,
  required = false,
  disabled = false,
  minDate,
  maxDate,
  pickerClassName = '',
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [useNativePicker, setUseNativePicker] = useState(shouldUseNativeDatePicker);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [pickerPosition, setPickerPosition] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Convert ISO date (yyyy-MM-dd) to display format (dd.MM.yyyy)
  const isoToDisplay = (isoDate: string): string => {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}.${month}.${year}`;
  };

  // Convert display format (dd.MM.yyyy) to ISO date (yyyy-MM-dd)
  const displayToISO = (display: string): string => {
    if (!display) return '';
    const parts = display.replace(/\D/g, ''); // Remove all non-digits

    if (parts.length < 8) return ''; // Incomplete date

    const day = parts.substring(0, 2);
    const month = parts.substring(2, 4);
    const year = parts.substring(4, 8);

    // Validate
    const dayNum = parseInt(day);
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);

    if (dayNum < 1 || dayNum > 31) return '';
    if (monthNum < 1 || monthNum > 12) return '';
    if (yearNum < 1900 || yearNum > 2100) return '';

    // Additional validation for valid date
    const date = new Date(yearNum, monthNum - 1, dayNum);
    if (date.getFullYear() !== yearNum || date.getMonth() !== monthNum - 1 || date.getDate() !== dayNum) {
      return '';
    }

    return `${year}-${month}-${day}`;
  };

  // Update display value when prop value changes
  useEffect(() => {
    setDisplayValue(isoToDisplay(value || ''));
    if (value) {
      const [year, month, day] = value.split('-');
      setSelectedDate(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)));
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const query = window.matchMedia(mobileDatePickerQuery);
    const handleQueryChange = () => {
      setUseNativePicker(query.matches);
      if (query.matches) {
        setShowPicker(false);
      }
    };

    handleQueryChange();
    query.addEventListener('change', handleQueryChange);

    return () => {
      query.removeEventListener('change', handleQueryChange);
    };
  }, []);

  // Update picker position
  const updatePickerPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const viewportPadding = 8;
      const minimumPickerWidth = 280;
      const pickerWidth = Math.min(Math.max(rect.width, minimumPickerWidth), window.innerWidth - viewportPadding * 2);
      const pickerHeight = 320;
      const hasRoomBelow = window.innerHeight - rect.bottom >= pickerHeight;

      setPickerPosition({
        top: hasRoomBelow ? rect.bottom + 4 : Math.max(viewportPadding, rect.top - pickerHeight - 4),
        left: Math.min(Math.max(rect.left, viewportPadding), window.innerWidth - pickerWidth - viewportPadding),
        width: pickerWidth,
      });
    }
  };

  // Close picker when clicking outside and handle scroll/resize
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowPicker(false);
      }
    };

    const handlePositionUpdate = () => {
      if (showPicker) {
        updatePickerPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handlePositionUpdate, true);
    window.addEventListener('resize', handlePositionUpdate);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handlePositionUpdate, true);
      window.removeEventListener('resize', handlePositionUpdate);
    };
  }, [showPicker]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;

    // Remove all non-digits
    const digits = input.replace(/\D/g, '');

    // Format as dd.MM.yyyy
    let formatted = '';
    if (digits.length > 0) {
      formatted = digits.substring(0, 2);
      if (digits.length > 2) {
        formatted += '.' + digits.substring(2, 4);
      }
      if (digits.length > 4) {
        formatted += '.' + digits.substring(4, 8);
      }
    }

    setDisplayValue(formatted);

    // Convert to ISO format and call onChange if valid
    if (digits.length === 8) {
      const isoDate = displayToISO(formatted);
      if (isoDate) {
        onChange(isoDate);
      } else {
        toast.error('Invalid date format. Please use dd.MM.yyyy', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } else if (digits.length === 0) {
      onChange('');
    }
  };

  const handleBlur = () => {
    // Validate and reformat on blur
    if (displayValue && displayValue.length > 0) {
      const isoDate = displayToISO(displayValue);
      if (isoDate) {
        setDisplayValue(isoToDisplay(isoDate));
        onChange(isoDate);
      } else if (displayValue.replace(/\D/g, '').length === 8) {
        toast.error('Invalid date. Please check day, month and year values.', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setDisplayValue('');
        onChange('');
      }
    }
  };

  const isDateDisabled = (date: Date): boolean => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const isoDate = `${year}-${month}-${day}`;

    if (minDate && isoDate < minDate) return true;
    if (maxDate && isoDate > maxDate) return true;
    return false;
  };

  const handleDateSelect = (date: Date) => {
    // Don't allow selecting disabled dates
    if (isDateDisabled(date)) return;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const isoDate = `${year}-${month}-${day}`;

    onChange(isoDate);
    setShowPicker(false);
  };

  const renderCalendar = () => {
    const currentDate = selectedDate || new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const prevMonthLastDay = new Date(year, month, 0).getDate();

    const days: (Date | null)[] = [];

    // Previous month days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push(new Date(year, month - 1, prevMonthLastDay - i));
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    // Next month days to fill the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push(new Date(year, month + 1, i));
    }

    const calendarElement = (
      <div
        ref={pickerRef}
        role="dialog"
        aria-label="Choose date"
        className={`date-picker-popover ${pickerClassName}`.trim()}
        style={{
          top: `${pickerPosition.top}px`,
          left: `${pickerPosition.left}px`,
          width: `${pickerPosition.width}px`,
        }}
      >
        {/* Header */}
        <div className="date-picker-header">
          <button
            type="button"
            onClick={() => setSelectedDate(new Date(year, month - 1, 1))}
            aria-label="Previous month"
            className="date-picker-nav-button"
          >
            ‹
          </button>
          <span className="date-picker-title">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
          <button
            type="button"
            onClick={() => setSelectedDate(new Date(year, month + 1, 1))}
            aria-label="Next month"
            className="date-picker-nav-button"
          >
            ›
          </button>
        </div>

        {/* Weekday headers */}
        <div className="date-picker-weekdays">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
            <div key={day} className="date-picker-weekday">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="date-picker-days">
          {days.map((date, index) => {
            if (!date) return null;
            const isCurrentMonth = date.getMonth() === month;
            const isSelected = selectedDate &&
              date.getDate() === selectedDate.getDate() &&
              date.getMonth() === selectedDate.getMonth() &&
              date.getFullYear() === selectedDate.getFullYear();
            const isToday =
              date.getDate() === new Date().getDate() &&
              date.getMonth() === new Date().getMonth() &&
              date.getFullYear() === new Date().getFullYear();
            const isDisabled = isDateDisabled(date);

            const dayClassName = [
              'date-picker-day',
              isSelected ? 'date-picker-day-selected' : '',
              isToday ? 'date-picker-day-today' : '',
              !isCurrentMonth ? 'date-picker-day-outside-month' : '',
            ].filter(Boolean).join(' ');

            return (
              <button
                key={`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`}
                type="button"
                onClick={() => handleDateSelect(date)}
                disabled={isDisabled}
                className={dayClassName}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );

    return ReactDOM.createPortal(calendarElement, document.body);
  };

  const handleFocus = () => {
    updatePickerPosition();
    setShowPicker(true);
  };

  if (useNativePicker) {
    return (
      <div className="date-input-wrapper">
        <input
          type="date"
          className={`${className} date-input date-input-native`}
          id={id}
          name={name}
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          min={minDate}
          max={maxDate}
          required={required}
          disabled={disabled}
        />
      </div>
    );
  }

  return (
    <div className="date-input-wrapper">
      <input
        ref={inputRef}
        type="text"
        className={`${className} date-input`}
        id={id}
        name={name}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onClick={handleFocus}
        placeholder="dd.MM.yyyy"
        maxLength={10}
        inputMode="numeric"
        autoComplete="off"
        aria-haspopup="dialog"
        required={required}
        disabled={disabled}
      />
      {showPicker && !disabled && renderCalendar()}
    </div>
  );
};

export default DateInput;
